'use client'

import type { ReactNode } from 'react'
import { QueryErrorResetBoundary } from '@tanstack/react-query'
import { SerwistProvider } from '@serwist/next/react'
import { TooltipProvider } from '@/components/ui/tooltip'
import { QueryProvider } from '@/providers/query-provider'
import { ThemeProvider } from '@/providers/theme-provider'
import { ErrorBoundary } from '@/components/errors/error-boundary'
import { ErrorFallback } from '@/components/errors/errorFallback'
import { GlobalErrorListener } from '@/components/errors/global-error-listener'
import { InstallAppButton } from '@/components/patterns/installAppButton'
import { OfflineBanner } from '@/components/patterns/offlineBanner'
import { PwaInstallListener } from '@/components/patterns/pwaInstallListener'
import { Toaster } from '@/components/patterns/toaster'
import type { Theme } from '@/stores/theme-store'

export function Providers({
  initialTheme,
  children,
}: {
  initialTheme: Theme
  children: ReactNode
}) {
  return (
    // `next.config.ts`'s `withSerwistInit` only *builds* `public/sw.js` — @serwist/next never
    // registers it client-side on its own, that's this provider's one job. Without it mounted
    // somewhere, no service worker is ever active, which fails Chrome's installability criteria
    // outright (manifest alone isn't enough) — the Android automatic install prompt silently never
    // fires. `disable` mirrors the exact same dev-only skip `next.config.ts` uses, since no sw.js
    // is even built in development — registering it there would just 404.
    <SerwistProvider swUrl="/sw.js" disable={process.env.NODE_ENV === 'development'}>
      <QueryProvider>
        <QueryErrorResetBoundary>
          {({ reset }) => (
            <ErrorBoundary tier="root" onReset={reset} label="the app" backHref="/">
              <ThemeProvider initialTheme={initialTheme}>
                <TooltipProvider delayDuration={200}>
                  <GlobalErrorListener />
                  <PwaInstallListener />
                  <InstallAppButton />
                  <OfflineBanner />
                  <Toaster />
                  {children}
                </TooltipProvider>
              </ThemeProvider>
            </ErrorBoundary>
          )}
        </QueryErrorResetBoundary>
      </QueryProvider>
    </SerwistProvider>
  )
}

export { ErrorFallback }
